import { Delete, DensityMediumOutlined } from '@mui/icons-material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Container, Typography } from '@mui/material';
import {
	DataGridPremium,
	GridActionsCellItem,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
	GridToolbarQuickFilter,
} from '@mui/x-data-grid-premium';
import { deDE } from '@mui/x-data-grid/locales';
import { LicenseInfo } from '@mui/x-license';
import { get } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MuiDialog from '../../../components/ui-component/mui-dialog/MuiDialog';
import MuiSnackbar from '../../../components/ui-component/mui-snackbar/MuiSnackbar';
import MUIToolTip from '../../../components/ui-component/mui-tooltip/MUIToolTip';
import { getLocalstorageToken } from '../../../context/AuthHelper';
import { DeleteAntrag, DuplicateAntrag } from '../../../lib/api/antrag';
import GetRecords from '../../../lib/api/GetRecords';
import { APIS } from '../../../lib/constants/apis';
import {
	ANTRAGS_ERFASSUNG_ART,
	ANTRAGS_FORMULAREN,
	ANTRAGS_STATUS,
	ANTRAG_TYP,
	ART_DES_ANTRAGS,
	MUIX_LICENSE,
} from '../../../lib/constants/constant';
import { PAGES_URLS } from '../../../lib/constants/routes';
import { DisplayDateTime } from '../../../utils/formats';
import {
	T,
	enumT,
	getDBKeysForEnum,
	getFieldNamefromDataPath,
	getLabelFromDataPath,
	isTrue,
	removeDataKeys,
	removeNullDeep,
} from '../../../utils/helper';

// import Schemas
import Schema from '../../../lib/paths/antrag_FULL_Paths.json';
let P = Schema.attributes;

// end-user-facing statuses only
const PORTAL_ANTRAGS_STATUS = [
	ANTRAGS_STATUS.inBearbeitung,
	ANTRAGS_STATUS.eingegangen,
	ANTRAGS_STATUS.inPruefung,
	ANTRAGS_STATUS.inUeberarbeitungDurchAntragssteller,
	ANTRAGS_STATUS.EmpfehlungZurUeberarbeitung,
	ANTRAGS_STATUS.EmpfehlungZurAblehnung,
	ANTRAGS_STATUS.EmpfehlungZurAntragstellung,
	ANTRAGS_STATUS.EmpfehlungZurFoerderung,
	ANTRAGS_STATUS.abgelehnt,
	ANTRAGS_STATUS.FinalisierungDurchAntragssteller,
	ANTRAGS_STATUS.eingegangenNachFinalisierung,
	ANTRAGS_STATUS.FoerderbescheidErstellt,
	ANTRAGS_STATUS.FoerderbescheidUebermittelt,
	ANTRAGS_STATUS.inFoerderung,
	ANTRAGS_STATUS.ProjektBeendet,
	ANTRAGS_STATUS.AntragDurchAenderungsantragAbgeloest,
	ANTRAGS_STATUS.freigegeben,
	ANTRAGS_STATUS.antragFreigeben,
	ANTRAGS_STATUS.durchFormantragAbgeloest,
];

LicenseInfo.setLicenseKey(MUIX_LICENSE);

function MeineAntraege() {
	const navigate = useNavigate();
	const location = useLocation();
	const [wsActive, setWsActive] = useState(true);
	const [wsError, setWsError] = useState(false);
	const [data, setData] = useState<any>([]);

	const [appIdToDelete, setAppIdToDelete] = useState(0);
	const [appDeleteSuccess, setAppDeleteSuccess] = useState<boolean | null>(null);

	const handleDuplicateApplication = async (id: number, antragsFormular: string, antragsTyp: string, isCopyMode: boolean = false) => {
		try {
			setWsActive(true);

			const antragFormular = getDBKeysForEnum(antragsFormular) as string;

			const res = await DuplicateAntrag(id, antragFormular, antragsTyp, undefined, undefined, isCopyMode);
			if (res) {
				navigate(`${PAGES_URLS.OnlineAntrag}`, {
					state: {
						id: get(res, 'data.id'),
						api: APIS.Antrag,
						antragsformular: antragFormular,
					},
				});
			}
		} catch (err: any) {
			console.error('ERROR: Duplicating Application ', err);
		} finally {
			setWsActive(false);
		}
	};

	const columns = useMemo(() => {
		// URL-Prüfung für Development-Umgebungen
		const isDevelopmentEnv = () => {
			const currentUrl = window.location.href;
			return currentUrl.includes('localhost') || currentUrl.includes('portal-dev') || currentUrl.includes('portal-qs');
		};

		let column: any[] = [
			...(isDevelopmentEnv() ?
				[
					{
						field: 'id',
						headerName: 'ID',
						maxWidth: 70,
					},
				]
			:	[]),
			{
				field: getFieldNamefromDataPath(P.Allgemein_Projekttitel.path),
				headerName: getLabelFromDataPath(P.Allgemein_Projekttitel.path),
				flex: 1,
				// width: 260,
			},
			{
				field: getFieldNamefromDataPath(P.Allgemein_Antragsformular.path),
				headerName: 'Förderprogramm',
				type: 'singleSelect',
				valueOptions: Object.values(ANTRAGS_FORMULAREN).map((program) => enumT(program)),
				flex: 1,
				maxWidth: 360,
			},
			{
				field: getFieldNamefromDataPath(P.Allgemein_Typ.path),
				headerName: getLabelFromDataPath(P.Allgemein_Typ.path),
				type: 'singleSelect',
				valueOptions: [T(ANTRAG_TYP.Antrag), T(ANTRAG_TYP.Skizze), T(ART_DES_ANTRAGS.Formantrag)],
				maxWidth: 100,
			},
			{
				field: getFieldNamefromDataPath(P.Antragstatus_Antragstatus.path),
				headerName: getLabelFromDataPath(P.Antragstatus_Antragstatus.path),
				type: 'singleSelect',
				valueOptions: PORTAL_ANTRAGS_STATUS.map((status) => {
					const label = enumT(status);
					// Both 'akzeptiert' and 'freigegeben' render as "Freigegeben"; disambiguate for the end user.
					if (status === ANTRAGS_STATUS.freigegeben) return { value: label, label: `${label} (Mittelabruf / Nachweise)` };
					if (status === ANTRAGS_STATUS.antragFreigeben) return { value: label, label: `${label} (Änderungsanfrage)` };
					return { value: label, label };
				}),
				flex: 1,
				maxWidth: 220,
			},
			// {
			// 	field: getFieldNamefromDataPath(P.Allgemein_Art_Des_Antrags.path),
			// 	headerName: getLabelFromDataPath(P.Allgemein_Art_Des_Antrags.path),
			// 	flex: 1,
			// 	maxWidth: 160,
			// },
			{
				field: getFieldNamefromDataPath(P.Allgemein_Eingereicht_Am.path),
				headerName: getLabelFromDataPath(P.Allgemein_Eingereicht_Am.path),
				flex: 1,
				maxWidth: 140,
			},
			{
				field: getFieldNamefromDataPath(P.Allgemein_Eingangsnummer.path),
				headerName: getLabelFromDataPath(P.Allgemein_Eingangsnummer.path),
				flex: 1,
				maxWidth: 140,
			},
			{
				field: getFieldNamefromDataPath(P.Skizze.path),
				headerName: getLabelFromDataPath(P.Skizze.path),
				flex: 1,
				maxWidth: 150,
			},
			{
				type: 'actions',
				field: 'actions',
				headerName: 'Aktion',
				width: 80,
				getActions: (params: any) => {
					let actions: any[] = [];
					const antragsTypRaw = get(params, 'row.Allgemein_Typ_raw', ANTRAG_TYP.Antrag);
					const skizzePathKey = getFieldNamefromDataPath(P.Skizze.path);
					const skizzeInRow = get(params, `row.${skizzePathKey}`, null);
					const skizzeErfassungsartPath = P.Skizze.schema.attributes.Allgemein_Erfassungsart.path.replace(P.Skizze.path + '.', '');
					const isSkizze = antragsTypRaw === ANTRAG_TYP.Skizze;
					const skizzeToCopyId = isSkizze ? get(params, 'row.id', -1) : get(skizzeInRow, 'id', -1);
					const skizzeErfassungsart = isSkizze ? get(params, 'row.Allgemein_Erfassungsart', '') : get(skizzeInRow, skizzeErfassungsartPath, '');
					const copySkizzeAction =
						skizzeToCopyId > 0 && skizzeErfassungsart === ANTRAGS_ERFASSUNG_ART.Manuell ?
							<MUIToolTip key='copy-disabled' title='Anträge mit externen Skizzen können nicht kopiert werden.'>
								<span>
									<GridActionsCellItem icon={<ContentCopyIcon />} label='Kopieren' onClick={() => {}} disabled={true} />
								</span>
							</MUIToolTip>
						: skizzeToCopyId > 0 ?
							<GridActionsCellItem
								key='copy-skizze'
								icon={<ContentCopyIcon />}
								label='Kopieren'
								onClick={() =>
									handleDuplicateApplication(skizzeToCopyId, get(params, 'row.Allgemein_Antragsformular', ''), ANTRAG_TYP.Skizze, true)
								}
							/>
						:	null;

					if (get(params, 'row.Antragstatus_Antragstatus') === enumT(ANTRAGS_STATUS.inBearbeitung)) {
						actions = [<GridActionsCellItem icon={<Delete />} label='Löschen' onClick={() => setAppIdToDelete(get(params, 'row.id', -1))} />];
						if (isDevelopmentEnv() && copySkizzeAction) {
							actions.push(copySkizzeAction);
						}
					} else if (get(params, 'row.Antragstatus_Antragstatus') !== enumT(ANTRAGS_STATUS.inBearbeitung) && isDevelopmentEnv()) {
						if (copySkizzeAction) {
							actions = [copySkizzeAction];
						} else {
							actions = [
								<GridActionsCellItem
									icon={<ContentCopyIcon />}
									label='Kopieren'
									onClick={() =>
										handleDuplicateApplication(get(params, 'row.id', -1), get(params, 'row.Allgemein_Antragsformular', ''), antragsTypRaw)
									}
								/>,
							];
						}
					}
					return actions;
				},
			},
		];
		return column;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function CustomToolbar() {
		return (
			<GridToolbarContainer>
				<GridToolbarFilterButton />
				<GridToolbarDensitySelector slotProps={{ button: { startIcon: <DensityMediumOutlined /> } }} />
				<GridToolbarQuickFilter sx={{ marginLeft: 'auto' }} />
			</GridToolbarContainer>
		);
	}

	const handleDeleteApp = async () => {
		try {
			setWsActive(true);
			let res = await DeleteAntrag(appIdToDelete);
			if (res.success) {
				setAppDeleteSuccess(true);
			} else {
				setAppDeleteSuccess(false);
				console.error('Partial failure during deletion ', res.errors);
			}
		} catch (err: any) {
			console.error('ERROR: Deleting Application ', err);
			setAppDeleteSuccess(false);
		} finally {
			setAppIdToDelete(0);
			setWsActive(false);
		}
	};

	useEffect(() => {
		const fetch = async () => {
			try {
				// TODO: Muss Überarbeitet werden, damit alle Anträge hier aufgelistet wird)
				let poplevel = [
					{ Skizze: 'id' },
					{ Skizze: 'Allgemein_Erfassungsart' },
					{ urspruenglicher_Antrag_Antrags: 'id' },
					{ urspruenglicher_Antrag_Antrags: 'Allgemein_Art_Des_Antrags' },
					{ urspruenglicher_Antrag: 'Allgemein_Art_Des_Antrags' },
					{ 'urspruenglicher_Antrag.Skizze': 'id' },
					{ 'urspruenglicher_Antrag.Skizze': 'Allgemein_Erfassungsart' },
				];
				let filter = {
					//Skizze: { id: { $null: true } },
					$or: [{ Allgemein_Typ: 'Antrag' }, { Allgemein_Typ: 'Skizze' }],
					$and: [{ Skizze_Antrags: { id: { $null: true } } }, { urspruenglicher_Antrag_Antrags: { id: { $null: true } } }],
					Antragstellende_Organisation: { id: get(getLocalstorageToken(), 'organisationId') },
				};
				let res = await GetRecords(APIS.Antrag, poplevel, filter, [{ updatedAt: 'desc' }]);
				const transformedData = removeNullDeep(removeDataKeys(get(res, 'data', []))).map((ele: Record<string, any>) => {
					const typ = get(ele, P.Allgemein_Typ.path);
					const artDesAntrags = get(ele, P.Allgemein_Art_Des_Antrags.path);
					const typAnzeige = typ === ANTRAG_TYP.Antrag && artDesAntrags === ART_DES_ANTRAGS.Formantrag ? ART_DES_ANTRAGS.Formantrag : typ;
					const relatedSkizze =
						typ === ANTRAG_TYP.Antrag ?
							artDesAntrags === ART_DES_ANTRAGS.Formantrag ?
								get(ele, `${P.urspruenglicher_Antrag.path}.${P.Skizze.path}`)
							:	get(ele, P.Skizze.path)
						:	null;

					return {
						id: get(ele, 'id'),
						Allgemein_Typ_raw: typ,
						[getFieldNamefromDataPath(P.Allgemein_Erfassungsart.path)]: T(get(ele, P.Allgemein_Erfassungsart.path)),
						[getFieldNamefromDataPath(P.Allgemein_Projekttitel.path)]: T(get(ele, P.Allgemein_Projekttitel.path)),
						[getFieldNamefromDataPath(P.Allgemein_Typ.path)]: T(typAnzeige),
						[getFieldNamefromDataPath(P.Allgemein_Antragsformular.path)]: enumT(get(ele, P.Allgemein_Antragsformular.path)),
						[getFieldNamefromDataPath(P.Antragstatus_Antragstatus.path)]: enumT(get(ele, P.Antragstatus_Antragstatus.path)),
						[getFieldNamefromDataPath(P.Allgemein_Art_Des_Antrags.path)]: T(get(ele, P.Allgemein_Art_Des_Antrags.path)),
						[getFieldNamefromDataPath(P.Allgemein_Art_Des_Antrags.path)]: enumT(get(ele, P.Allgemein_Art_Des_Antrags.path, '')),
						[getFieldNamefromDataPath(P.Allgemein_Eingereicht_Am.path)]:
							get(ele, P.Allgemein_Eingereicht_Am.path) ? DisplayDateTime(get(ele, P.Allgemein_Eingereicht_Am.path)) : '-',
						[getFieldNamefromDataPath(P.Allgemein_Eingangsnummer.path)]: T(get(ele, P.Allgemein_Eingangsnummer.path)),
						[getFieldNamefromDataPath(P.Skizze.path)]: relatedSkizze,
					};
				});
				setData(transformedData);
				setWsActive(true);
			} catch (err: any) {
				console.error('ERROR: Fetching Applications ', err);
				setWsError(true);
			} finally {
				setWsActive(false);
			}
		};

		fetch();
	}, [appDeleteSuccess]);

	if (wsError) {
		return <Typography>Etwas is schief gelaufen</Typography>;
	}

	return (
		<>
			<Container>
				<MuiSnackbar
					showSnackbar={isTrue(appDeleteSuccess)}
					closeSnackbar={() => setAppDeleteSuccess(null)}
					alertMessage='Der Antrag wurde erfolgreich gelöscht'
				/>
				<MuiSnackbar
					showSnackbar={appDeleteSuccess === false}
					closeSnackbar={() => setAppDeleteSuccess(null)}
					alertMessage='Der Antrag konnte nicht gelöscht werden'
				/>
				<MuiDialog
					open={appIdToDelete > 0}
					title={'Antrag Löschen?'}
					content={'Wollen Sie den Antrag wirklich löschen? Diese Aktion ist unwiderruflich.'}
					onClose={() => setAppIdToDelete(0)}
					onConfirm={() => handleDeleteApp()}
				/>
				<Typography variant='h3'> Meine Anträge</Typography>
			</Container>
			<Box sx={{ p: '1.5rem', maxWidth: '90rem', boxSizing: 'border-box', margin: 'auto' }}>
				<DataGridPremium
					loading={wsActive}
					autoHeight
					pagination
					pageSizeOptions={[5, 10, 25, 50, 100]}
					rows={data}
					columns={columns}
					columnVisibilityModel={{ Skizze: false }}
					disableColumnSelector
					onRowClick={(param) => {
						navigate(`${PAGES_URLS.FoerderDashboard}`, {
							state: {
								id: param.row.Skizze ? param.row.Skizze.id : param.id,
								antragsFormular: getDBKeysForEnum(get(param, `row.${getFieldNamefromDataPath(P.Allgemein_Antragsformular.path)}`)),
							},
						});
					}}
					initialState={{
						filter: {
							filterModel: get(location, 'state.filter', { items: [] }),
						},

						pagination: {
							paginationModel: { pageSize: 10, page: 0 },
						},
					}}
					localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
					slots={{ toolbar: CustomToolbar }}
				/>
			</Box>
		</>
	);
}

export default MeineAntraege;
